/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  type CreateFollowUpPayload,
  type FollowUp,
  type FollowUpAlerts,
  type FollowUpAnalytics,
  type FollowUpsQuery,
  type UpdateFollowUpPayload,
  followUpsApi,
} from '../api/follow-ups.js';

const KEY = ['follow-ups'] as const;

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
  };
}

export function useFollowUps(query: FollowUpsQuery = {}): UseQueryResult<readonly FollowUp[]> {
  return useQuery({ queryKey: [...KEY, 'list', query], queryFn: () => followUpsApi.list(query) });
}

export function useFollowUpAlerts(): UseQueryResult<FollowUpAlerts> {
  return useQuery({ queryKey: [...KEY, 'alerts'], queryFn: () => followUpsApi.alerts() });
}

export function useFollowUpAnalytics(): UseQueryResult<FollowUpAnalytics> {
  return useQuery({ queryKey: [...KEY, 'analytics'], queryFn: () => followUpsApi.analytics() });
}

export function useCreateFollowUp(): UseMutationResult<FollowUp, Error, CreateFollowUpPayload> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (p: CreateFollowUpPayload) => followUpsApi.create(p),
    onSuccess: invalidate,
  });
}

export function useUpdateFollowUp(): UseMutationResult<
  FollowUp,
  Error,
  { id: string; payload: UpdateFollowUpPayload }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFollowUpPayload }) =>
      followUpsApi.update(id, payload),
    onSuccess: invalidate,
  });
}

export function useResolveFollowUp(): UseMutationResult<
  FollowUp,
  Error,
  { id: string; resolutionNote: string }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, resolutionNote }: { id: string; resolutionNote: string }) =>
      followUpsApi.resolve(id, resolutionNote),
    onSuccess: invalidate,
  });
}
